import { useContext, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import YourContactBox from "../ContactBox/ContactBox";
import { OrderSummary } from "../OrderSummary";
import { AdminOrderContext } from "../../context/Admin/Order";
import { Typography } from "@mui/material";
import {
  EditableSelectInput,
  FlexColBox,
  GradientBorderedBox,
} from "../../common/components";
import {
  AddressTypeEnum,
  Currency,
  CURRENCY,
  PreOrderPaymentOptions,
} from "../../types";
import { createStyles, palette } from "../../styles/theme";
import AddressIdenticalIndicator from "./components/AddressIdenticalIndicator";
import { EditableTextInput } from "../../common/components/Input/EditableTextInput";
import EditableInputAddressField from "./components/EditableInputAddressField";
import { useMoment } from "../../common/hooks/useMoment";
import { isQuoteRequest } from "../../utils/CartUtils";
import { useLocation } from "react-router-dom";
import { OrderContext } from "../../context/Order/OrderContext";
import { useAuth } from "../../common/hooks/useAuth";

const styles = createStyles({
  block: {
    p: "24px",
    backgroundColor: palette.white,
    borderRadius: "8px",
    border: `1px solid ${palette.grey.lightGrey}`,
    gap: "24px",
  },
});

const OrderDetails = ({
  showDistributorPrices,
}: {
  showDistributorPrices: boolean;
}) => {
  const location = useLocation();
  const isFromAdmin = location.pathname.includes("/admin");
  const { isAdmin } = useAuth();
  const { t } = useTranslation();
  const { formatFullDateWithTime } = useMoment();
  const {
    orderDetail: preorderDetailFromAdmin,
    onUpdateProjectNumber,
    onUpdatePoNumber,
    onUpdateShippingAddress,
    onUpdateBillingAddress,
    onUpdateCurrency,
    onUpdateClientDetailsField,
  } = useContext(AdminOrderContext);
  const { orderDetail: preorderDetailFromUser } = useContext(OrderContext);

  const preorderDetail = isFromAdmin
    ? preorderDetailFromAdmin
    : preorderDetailFromUser;

  const currencyOptions = useMemo(
    () =>
      Object.values(CURRENCY).map((currency) => {
        return {
          value: currency,
          label: t(`order.currency.options.${currency}`),
          disabled: false,
        };
      }),
    [t]
  );

  const hideOrderSummary =
    (isQuoteRequest(preorderDetail?.pricing, preorderDetail?.techpack) ||
      preorderDetail?.pricing.productsTotal.toString() === "TBD" ||
      preorderDetail?.pricing.shippingTotal.toString() === "TBD") &&
    !isFromAdmin;

  if (!preorderDetail) {
    return null;
  }

  return (
    <FlexColBox
      gap="12px"
      maxWidth={{ xs: "100%", md: "80%", lg: "30%" }}
      width="100%"
      fullWidth
      sx={{
        "@media print": {
          maxWidth: "100%",
          "& .editable-control-icon": {
            display: "none !important",
          },
        },
      }}
    >
      <FlexColBox sx={styles.block}>
        <Typography variant="textLargeBold">
          {t("order.order_details")}
        </Typography>

        <FlexColBox gap="24px" fullWidth>
          {!isFromAdmin && !preorderDetail?.techpack?.projectNumber ? null : (
            <EditableTextInput
              inline={false}
              boldLabel
              fullWidth
              autoFocus
              name="projectNumber"
              defaultValue={
                preorderDetail?.techpack?.projectNumber ||
                (isQuoteRequest(
                  preorderDetail?.pricing,
                  preorderDetail?.techpack
                )
                  ? t("order.quote_request")
                  : "")
              }
              onConfirm={onUpdateProjectNumber}
              label={t("order.project_number")}
              readOnly={!isFromAdmin}
            />
          )}
          {!isFromAdmin && !preorderDetail?.poNumber ? null : (
            <EditableTextInput
              inline={false}
              autoFocus
              boldLabel
              fullWidth
              name="poNumber"
              defaultValue={preorderDetail?.poNumber ?? ""}
              onConfirm={onUpdatePoNumber}
              label={t("order.po_number")}
              readOnly={!isFromAdmin}
            />
          )}
          {!isFromAdmin && !preorderDetail?.notes ? null : (
            <FlexColBox gap="8px">
              <Typography variant="textRegularBold">
                {t("order.customer_note")}
              </Typography>
              {preorderDetail?.notes ? (
                <GradientBorderedBox
                  liteBorder
                  forceGradient
                  borderWidth="1px"
                  borderWidthWhenActive="1px"
                  sx={{
                    p: "12px",
                    borderRadius: "8px",
                    cursor: "default",
                    pointerEvents: "none",
                  }}
                >
                  <Typography variant="textRegular">
                    {preorderDetail?.notes || <>&ndash;</>}
                  </Typography>
                </GradientBorderedBox>
              ) : (
                <Typography variant="textRegular">&ndash;</Typography>
              )}
            </FlexColBox>
          )}
          <FlexColBox gap="8px">
            <Typography variant="textRegularBold">
              {t("general.date")}
            </Typography>
            <Typography variant="textRegular" color={palette.grey.primary}>
              {formatFullDateWithTime(
                preorderDetail?.preorder?.createdAt ?? ""
              )}
            </Typography>
          </FlexColBox>
        </FlexColBox>
      </FlexColBox>

      <FlexColBox sx={styles.block}>
        <Typography variant="textLargeBold">
          {t("order.contact_details")}
        </Typography>
        <FlexColBox gap="24px">
          <YourContactBox userDetails={preorderDetail?.user} fromAdmin={true} />
          {!isFromAdmin && !preorderDetail?.clientDetails?.clientName ? null : (
            <EditableTextInput
              inline={false}
              autoFocus
              boldLabel
              fullWidth
              name="clientName"
              defaultValue={preorderDetail?.clientDetails?.clientName ?? ""}
              onConfirm={(value) =>
                onUpdateClientDetailsField?.("clientName", value)
              }
              label={t("checkout.client_details.client_name")}
              readOnly={!isFromAdmin}
            />
          )}
          {!isFromAdmin &&
          !preorderDetail?.clientDetails?.clientNumber ? null : (
            <EditableTextInput
              inline={false}
              autoFocus
              boldLabel
              fullWidth
              name="clientNumber"
              defaultValue={preorderDetail?.clientDetails?.clientNumber ?? ""}
              onConfirm={(value) =>
                onUpdateClientDetailsField?.("clientNumber", value)
              }
              label={t("checkout.client_details.client_number")}
              readOnly={!isFromAdmin}
            />
          )}
          {!isFromAdmin &&
          !preorderDetail?.clientDetails?.clientProjectName ? null : (
            <EditableTextInput
              inline={false}
              autoFocus
              boldLabel
              fullWidth
              name="clientProjectName"
              defaultValue={
                preorderDetail?.clientDetails?.clientProjectName ?? ""
              }
              onConfirm={(value) =>
                onUpdateClientDetailsField?.("clientProjectName", value)
              }
              label={t("checkout.client_details.client_project_name")}
              readOnly={!isFromAdmin}
            />
          )}
        </FlexColBox>
      </FlexColBox>

      <FlexColBox sx={styles.block}>
        <Typography variant="textLargeBold">{t("order.addresses")}</Typography>
        <FlexColBox gap="24px">
          {isAdmin && (
            <AddressIdenticalIndicator preorderDetail={preorderDetail} />
          )}
          <FlexColBox gap="16px">
            <EditableInputAddressField
              initialValue={preorderDetail.shippingAddress}
              onUpdate={onUpdateShippingAddress}
              titleLabel={t("order.delivery")}
              addressType={AddressTypeEnum.SHIPPING}
              fromAdmin={isFromAdmin}
            />
            <EditableInputAddressField
              initialValue={preorderDetail.billingAddress}
              onUpdate={onUpdateBillingAddress}
              titleLabel={t("order.billing")}
              addressType={AddressTypeEnum.BILLING}
              fromAdmin={isFromAdmin}
            />
          </FlexColBox>
        </FlexColBox>
      </FlexColBox>

      {!hideOrderSummary && (
        <FlexColBox sx={styles.block}>
          <Typography variant="textLargeBold">
            {t("order.order_summary")}
          </Typography>
          <FlexColBox gap="24px">
            <EditableSelectInput
              inline={false}
              boldLabel
              fullWidth
              valueColor={palette.grey.primary}
              name="currency"
              defaultValue={preorderDetail.pricing.currency}
              options={currencyOptions}
              onConfirm={(value) => onUpdateCurrency?.(value as Currency)}
              label={t("order.currency.title")}
              readOnly={!isFromAdmin}
            />
            <FlexColBox gap="8px">
              <Typography variant="textRegularBold">
                {t("order.payment_option")}
              </Typography>
              <Typography variant="textRegular" color={palette.grey.primary}>
                <Trans
                  i18nKey={`checkout.payment_options.${preorderDetail.paymentOption ?? PreOrderPaymentOptions.NOT_SET}`}
                  components={{
                    1: (
                      <Typography
                        variant="textXs"
                        color={palette.grey.primary}
                      />
                    ),
                  }}
                />
              </Typography>
            </FlexColBox>
            <FlexColBox gap="8px">
              <Typography variant="textRegularBold">
                {t("order.order_summary")}
              </Typography>
              <OrderSummary
                orderDetail={preorderDetail}
                showDistributorPrices={showDistributorPrices}
              />
            </FlexColBox>
          </FlexColBox>
        </FlexColBox>
      )}

      {/* <Box >
        <EditableInputSelectField
          initialValue={preorderDetail?.pricing?.carbonNeutral?.toString()}
          onUpdate={onUpdateCarbonNeutral}
          options={[
            { value: "true", label: t("general.yes"), disabled: false },
            { value: "false", label: t("general.no"), disabled: false },
          ]}
          titleLabel={t("order.carbon_neutral")}
          emptyLabel={t("general.no")}
        />
      </Box> */}
    </FlexColBox>
  );
};

export default OrderDetails;
